import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Contract, parseEther, formatEther, MaxUint256 } from "ethers";
import { useWallet } from "@/context/WalletContext";
import { Panel, Pill } from "@/components/croc/ui";
import { fmtNum } from "@/lib/format";
import api from "@/lib/api";
import { CONTRACTS, ABIS, CHAIN } from "@/contracts/croc";
import { Repeat, Flame, Coins, Radio } from "lucide-react";

const ERC20_ABI = [
  "function approve(address spender, uint256 value) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function balanceOf(address) view returns (uint256)",
];

export const ExchangePanel = ({ tierIndex = 0 }) => {
  const { getBrowserProvider, ensureTestnet, refreshUser } = useWallet();
  const [side, setSide] = useState("buy");
  const [amount, setAmount] = useState("");
  const [quote, setQuote] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.exchangeInfo().then(setInfo).catch(() => {}); }, []);
  useEffect(() => {
    const n = parseFloat(amount);
    if (!n || n <= 0) return setQuote(null);
    const t = setTimeout(() => api.exchangeQuote(side, n).then(setQuote).catch(() => setQuote(null)), 250);
    return () => clearTimeout(t);
  }, [amount, side]);

  const run = async () => {
    const provider = getBrowserProvider();
    if (!provider) return toast.error("Connect a real wallet (MetaMask/WalletConnect) to trade");
    const n = parseFloat(amount);
    if (!n || n <= 0) return toast.error("Enter an amount");
    try {
      setBusy(true);
      await ensureTestnet();
      const signer = await provider.getSigner();
      const me = await signer.getAddress();
      const ex = new Contract(CONTRACTS.CrocExchange, ABIS.CrocExchange, signer);
      const wei = parseEther(String(n));
      if (side === "buy") {
        const usdt = new Contract(CONTRACTS.MockUSDT, ERC20_ABI, signer);
        const allowance = await usdt.allowance(me, CONTRACTS.CrocExchange);
        if (allowance < wei) {
          toast.info("Approve USDT…");
          await (await usdt.approve(CONTRACTS.CrocExchange, MaxUint256)).wait();
        }
        toast.info("Buying CROC…");
        await (await ex.buy(wei, tierIndex)).wait();
        toast.success(`Bought CROC with ${n} USDT`);
      } else {
        const croc = new Contract(CONTRACTS.CrocToken, ERC20_ABI, signer);
        const allowance = await croc.allowance(me, CONTRACTS.CrocExchange);
        if (allowance < wei) {
          toast.info("Approve CROC…");
          await (await croc.approve(CONTRACTS.CrocExchange, MaxUint256)).wait();
        }
        toast.info("Selling & burning CROC…");
        await (await ex.sell(wei)).wait();
        toast.success(`Sold ${n} CROC for USDT`);
      }
      setAmount("");
      refreshUser?.();
      api.exchangeInfo().then(setInfo).catch(() => {});
    } catch (e) {
      toast.error(e?.shortMessage || e?.reason || "Transaction failed");
    } finally {
      setBusy(false);
    }
  };

  const inToken = side === "buy" ? "USDT" : "CROC";
  return (
    <Panel data-testid="exchange-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Repeat className="w-4 h-4 emerald-text" />
          <span className="font-display font-bold text-white">Buy / Sell CROC · USDT</span>
        </div>
        <Pill tone="emerald"><Radio className="w-3 h-3" /> On-chain</Pill>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <button data-testid="exch-tab-buy" onClick={() => setSide("buy")}
          className={`rounded-xl py-2.5 font-semibold transition-colors ${side === "buy" ? "bg-[#1CFD4C] text-[#050806]" : "bg-[#0B120E] text-muted-foreground border border-white/10"}`}>Buy</button>
        <button data-testid="exch-tab-sell" onClick={() => setSide("sell")}
          className={`rounded-xl py-2.5 font-semibold transition-colors ${side === "sell" ? "bg-[#10B981] text-[#050806]" : "bg-[#0B120E] text-muted-foreground border border-white/10"}`}>Sell &amp; Burn</button>
      </div>

      <label className="text-xs text-muted-foreground">You pay ({inToken})</label>
      <input data-testid="exch-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
        placeholder="0.00"
        className="w-full mt-1 rounded-xl border border-white/10 bg-[#090E0B] px-4 py-3 font-num text-white text-lg outline-none focus:border-[#1CFD4C]/50" />

      {quote?.ok && (
        <div className="mt-3 rounded-xl border border-white/10 bg-[#090E0B] p-3 space-y-1.5 text-sm" data-testid="exch-quote">
          {side === "buy" ? (
            <>
              <Row label="You receive" value={`${fmtNum(quote.croc_out, 2)} CROC`} accent />
              <Row label="Buy fee" value={`${fmtNum(quote.fee_usdt, 2)} USDT`} />
            </>
          ) : (
            <>
              <Row label="You receive" value={`${fmtNum(quote.net_usdt, 2)} USDT`} accent />
              <Row label="Admin deduction" value={`${fmtNum(quote.fee_usdt, 2)} USDT`} />
              <Row label="CROC burned" value={`${fmtNum(quote.croc_in, 2)} 🔥`} />
            </>
          )}
          <Row label="Rate" value={`1 CROC = $${fmtNum(quote.price, 5)}`} />
        </div>
      )}

      <button data-testid="exch-submit" onClick={run} disabled={busy}
        className={`w-full mt-4 rounded-xl py-3 font-semibold text-[#050806] transition-colors disabled:opacity-60 ${side === "buy" ? "bg-[#1CFD4C] hover:bg-[#4DFF74]" : "bg-[#10B981] hover:bg-[#34D399]"}`}>
        {busy ? "Processing…" : side === "buy" ? "Approve & Buy CROC" : "Approve, Sell & Burn"}
      </button>

      {info && (
        <div className="mt-4 flex items-center justify-between text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1"><Coins className="w-3 h-3" /> Liquidity {fmtNum(info.liquidity_usdt, 0)} USDT</span>
          <span className="flex items-center gap-1"><Flame className="w-3 h-3 gold-text" /> Burned {fmtNum(info.total_burned_croc, 0)} CROC</span>
          <a href={`${CHAIN.explorer}/address/${info.exchange_address}`} target="_blank" rel="noreferrer" className="emerald-text hover:underline">Contract ↗</a>
        </div>
      )}
    </Panel>
  );
};

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-num ${accent ? "emerald-text font-bold" : "text-white"}`}>{value}</span>
    </div>
  );
}
